/**
 * What: Renders the merchant recovery action after a server-enforced credit ceiling.
 * Transport / data-flow: Receives the validated credit balance and quoted run cost
 * from the run endpoint; it never calculates an allowance or reset date in the browser.
 *
 */
import { shopifyComponentCopy } from '@testfeed/product-language'
import { CREDITS_ART } from './imagery/houseArt'
import type { CreditSpendQuote } from './credit-spend-prompt'
import { useAppBridge } from '@shopify/app-bridge-react'
import { trackShopifyAction } from '../shopifyUiTracking'

function formatResetDate(resetsAt: string) {
  return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric', timeZone: 'UTC' }).format(new Date(resetsAt))
}

/**
 * Presents the billable path as an illustrated prompt and the non-billable path as a plain notice,
 * so a plan the merchant cannot change never renders an upgrade offer.
 */
export function CreditExhaustedPrompt({ spend }: { spend: CreditSpendQuote }) {
  const shopify = useAppBridge()
  const copy = shopifyComponentCopy.planBilling
  const resetDate = copy.limitResetDate.replace('{date}', formatResetDate(spend.balance.resetsAt))
  const detail = copy.limitReachedDetail
    .replace('{quoted}', String(spend.quotedCredits))
    .replace('{available}', String(spend.balance.availableCredits))

  if (spend.balance.plan.kind !== 'shopify') {
    return (
      <s-banner heading={copy.limitReachedTitle} tone="warning">
        <s-paragraph>{copy.limitReachedBody}</s-paragraph>
        <s-paragraph>{detail}</s-paragraph>
        <s-paragraph>{resetDate}</s-paragraph>
      </s-banner>
    )
  }

  return (
    <s-section>
      <s-stack direction="inline" gap="large" alignItems="center">
        <s-box inlineSize="160px" blockSize="120px">
          <s-image src={CREDITS_ART} alt="" objectFit="contain" />
        </s-box>
        <s-stack direction="block" gap="base">
          <s-heading>{copy.limitReachedTitle}</s-heading>
          <s-paragraph>{copy.limitReachedBody}</s-paragraph>
          <s-paragraph>{detail}</s-paragraph>
          <s-paragraph>{resetDate}</s-paragraph>
          <s-stack direction="inline">
            <s-button
              href="shopify://admin/settings/billing"
              onClick={() => trackShopifyAction(shopify, 'choose-plan', () => undefined)}
            >
              {shopifyComponentCopy.testFeedReadinessCard.managePlan008}
            </s-button>
          </s-stack>
        </s-stack>
      </s-stack>
    </s-section>
  )
}
